import React, { useCallback, useEffect, useMemo, useState } from "react";
import { getUser } from "../../../services/utils";
import { api } from "../../../services/api";
import { AdminPermission, AdminUser } from "../AdminUserList.types";

export type AdminUsersProviderProps = {
  actorUserId?: string;
  adminUsers: AdminUser[];
  myPermissions: AdminPermission[];
  refresh: () => {}
  fetchMyPermissions: () => {}
  initialPermissionsLoaded: boolean
}

export const AdminUsersContext = React.createContext<AdminUsersProviderProps>({
  adminUsers: [],
  myPermissions: [],
  refresh: () => ({}),
  fetchMyPermissions: () => ({}),
  initialPermissionsLoaded: false
});

export function AdminUsersProvider({ children }) {
  const user = getUser();
  const actorUserId: string | undefined = typeof user?.id === 'string' ? user.id : undefined;

  const [adminUsers, setAdminUsers] = useState<Array<AdminUser>>([]);
  const [myPermissions, setMyPermissions] = useState<Array<AdminPermission>>([]);
  const [initialPermissionsLoaded, setInitialPermissionsLoaded] = useState(false);

  const fetchMyPermissions = useCallback(async () => {
    try {
      const newMyPermissionsResp = (await api.getMyAdminPermissions());
      setMyPermissions(newMyPermissionsResp.data.permissions as AdminPermission[])
      setInitialPermissionsLoaded(true)
    } catch (err: any) {
      if (err?.status === 401 && window.location.pathname !== "/login") {
        window.location.replace("/login");
      }
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    // Avoid firing the admin-users request before a user is authenticated.
    if (user?.id && initialPermissionsLoaded) {
      try {
        setAdminUsers(await api.getAdminUsers())
      } catch (err) {
        // Silently fail if user doesn't have access
        console.error('Failed to fetch admin users:', err);
      }
    }
  }, [initialPermissionsLoaded, user?.id]);

  const refresh = useCallback(async () => {
    fetchUsers();
    fetchMyPermissions();
  }, [fetchMyPermissions, fetchUsers])

  useEffect(() => {
    if (user?.id) {
      fetchMyPermissions();
    }
  }, [user?.id]);

  useEffect(() => {
    if (user?.id && initialPermissionsLoaded) {
      fetchUsers();
    }
  }, [fetchUsers, initialPermissionsLoaded, user?.id]);

  const values = useMemo(() => ({
    actorUserId,
    adminUsers: adminUsers,
    myPermissions: myPermissions,
    refresh: refresh,
    fetchMyPermissions,
    initialPermissionsLoaded
  }), [actorUserId, adminUsers, fetchMyPermissions, initialPermissionsLoaded, myPermissions, refresh]);

  return <AdminUsersContext.Provider value={values}>{children}</AdminUsersContext.Provider>;
}

export function useAdminUsers() {
  const context = React.useContext<AdminUsersProviderProps>(AdminUsersContext);
  if (context === undefined) {
    throw new Error("useAdminUsers must be used within an AdminUsersProvider");
  }
  return context;
}
